import { useState, useEffect, useMemo, CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Sparkles, Search, ChefHat, Clock, Users, Trash2, GripVertical } from 'lucide-react';
import { rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  xxLargeTextSize,
  tertiaryText,
  baseTextSize,
} from '../../../styling/stylingHelper';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useRecipeStore } from '../../../stores/appRecipeStore';
import { useImpersonationStore } from '../../../stores/appImpersonationStore';
import { useLoadRecipes, useLoadMenus, useLoadRevenueHistory } from '../../../utils/dataLoadingUtils';
import { useMenusStore } from '../../../stores/appMenusStore';
import { useFiltersStore, RECIPES_UNASSIGNED_CATEGORY } from '../../../stores/appFiltersStore';
import { useCostPriceCategoriesStore } from '../../../stores/appCostPriceCategoriesStore';
import { CostPriceCategory } from '../../../services/costPrice/costPriceCategoriesService';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { getActiveDishNames, getCostPriceMenuMode } from '../../../utils/costPriceMenuFilter';
import { CategoryBadge } from '../../../utils/categoryHelper';
import { SortableList } from '../../common/SortableList';
import MenuFilterDropdown from '../../menus/MenuFilterDropdown';
import SaveRecipeModal from '../../recipes/SaveRecipeModal';
import RecipeAutomationModal from './modals/RecipeAutomationModal';
import RecipeDeleteAllModal from './modals/RecipeDeleteAllModal';
import { useAuth } from '../../../services/authentication/authContext';
import { Navigate } from 'react-router-dom';
import { canAccessRecipesPage, canAccessRecipeAutomation, canUseSuperAdminFeatures } from '../../../services/accessManagement/accessManagement';
import { Recipe, getTranslatedText, SupportedLanguage, updateRecipePositions } from '../../../services/recipes/recipesService';
import { toTranslationKey } from '../../../types/RecipeEnums';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { RecipeViewerMenu } from '../recipeViewer/RecipeViewerMenu';

const RecipesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { backendUser, accountSettings } = useAuth();
  const isImpersonating = useImpersonationStore((s) => s.target !== null);

  const selectedRestaurant = useAppUserDefaultStore((s) => s.restaurant);
  const restaurantId = selectedRestaurant?.id;

  // Use store for recipes
  const recipes = useRecipeStore((s) => s.recipes);
  const isLoading = useRecipeStore((s) => s.isLoadingList);
  const reloadRecipes = useRecipeStore((s) => s.reloadRecipes);
  const applyPositions = useRecipeStore((s) => s.applyPositions);

  // Load recipes, menus and revenue history (revenue needed for Active Dishes mode)
  useLoadRecipes();
  useLoadMenus();
  useLoadRevenueHistory();

  // Cost price categories — useLoadRecipes() does not load them, and without them
  // the badge has no colour and the chip row no names. Categories only (not the
  // full cost price analyses) is all this page needs.
  const categories = useCostPriceCategoriesStore((s) => s.categories);
  const loadCategories = useCostPriceCategoriesStore((s) => s.load);
  const isCategoriesLoadedFor = useCostPriceCategoriesStore((s) => s.isDataLoadedFor);

  useEffect(() => {
    if (!restaurantId) return;
    if (selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant)) return;
    if (isCategoriesLoadedFor(restaurantId)) return;
    loadCategories(restaurantId);
  }, [restaurantId, selectedRestaurant, isCategoriesLoadedFor, loadCategories]);

  // Menu filter state (multi-select, same semantics as cost price page)
  const menus = useMenusStore((s) => s.menus);
  const menusCurrentRestaurantId = useMenusStore((s) => s.currentRestaurantId);
  const menuItemNames = useMenusStore((s) => s.menuItemNames);
  const loadMenuItemNames = useMenusStore((s) => s.loadMenuItemNames);
  const menuFilters = useFiltersStore((s) => s.recipesMenuFilters);
  const setMenuFilters = useFiltersStore((s) => s.setRecipesMenuFilters);
  const menuFiltersInitForRestaurantId = useFiltersStore((s) => s.recipesMenuFiltersInitForRestaurantId);
  const setMenuFiltersInitForRestaurantId = useFiltersStore((s) => s.setRecipesMenuFiltersInitForRestaurantId);
  const menuMode = getCostPriceMenuMode(menuFilters);
  const revenueHistoryStore = useRevenueHistory();

  // Category filter state (single-select, same semantics as the cost price chip row)
  const selectedCategoryId = useFiltersStore((s) => s.recipesCategoryFilter);
  const setSelectedCategoryId = useFiltersStore((s) => s.setRecipesCategoryFilter);

  // Initialize per-restaurant: active menus, or Active Dishes if none are active.
  // Only runs once the menus store has finished loading for THIS restaurant
  // (otherwise we'd lock in a default while menus is still the initial empty array).
  // Init gate lives in the filters store so navigating away & back preserves the
  // user's manual selection (a local ref would reset on remount).
  useEffect(() => {
    if (!restaurantId) return;
    if (selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant)) return;
    if (menusCurrentRestaurantId !== restaurantId) return;
    if (menuFiltersInitForRestaurantId === restaurantId) return;

    const activeMenuIds = menus.filter((m) => m.isActive).map((m) => m.id);
    setMenuFilters(activeMenuIds.length > 0 ? activeMenuIds : ['__active_dishes__']);
    setMenuFiltersInitForRestaurantId(restaurantId);
  }, [restaurantId, selectedRestaurant, menus, menusCurrentRestaurantId, menuFiltersInitForRestaurantId, setMenuFilters, setMenuFiltersInitForRestaurantId]);

  // Ensure menuItemNames are loaded for every selected real menu.
  useEffect(() => {
    if (menuMode !== 'menus') return;
    menuFilters.forEach((id) => {
      loadMenuItemNames(id);
    });
  }, [menuMode, menuFilters, loadMenuItemNames]);

  const activeDishNames = useMemo(
    () => getActiveDishNames(revenueHistoryStore.revenueHistoryList),
    [revenueHistoryStore.revenueHistoryList]
  );

  // Set of menu item names from all currently selected real menus
  const selectedMenuItemNames = useMemo(() => {
    if (menuMode !== 'menus') return null;
    const names = new Set<string>();
    menuFilters.forEach((id) => {
      (menuItemNames[id] || []).forEach((name) => names.add(name));
    });
    return names;
  }, [menuMode, menuFilters, menuItemNames]);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showAutomationModal, setShowAutomationModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Check if "All Restaurants" is selected
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

  // Check if user has access to recipes page
  const hasAccess = canAccessRecipesPage(backendUser, accountSettings);
  // Check if user has access to recipe automation
  const hasRecipeAutomationAccess = canAccessRecipeAutomation(backendUser, accountSettings);
  // Bulk deletion is reserved for super admins impersonating an account.
  const canDeleteAllRecipes = isImpersonating && canUseSuperAdminFeatures(backendUser);

  // Recipes passing the menu filter only. This is the base for the category chip
  // row, so the chips reflect what the menu filter left — not what the (transient)
  // search box is currently narrowing, which would make them flicker while typing.
  const menuFilteredRecipes = useMemo(() => recipes.filter((recipe) => {
    if (menuMode === 'active_dishes') {
      return !!recipe.menuItemName && activeDishNames.has(recipe.menuItemName);
    }
    if (selectedMenuItemNames) {
      return !!recipe.menuItemName && selectedMenuItemNames.has(recipe.menuItemName);
    }
    return true;
  }), [recipes, menuMode, activeDishNames, selectedMenuItemNames]);

  // Colour/name lookup for the card badge.
  const categoryById = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories]
  );

  // The restaurant's default category is literally named "Unassigned"
  // (create_db.sql:319). A recipe linked to it and a recipe with no category at
  // all mean the same thing to the user, so both live under ONE "Unassigned"
  // chip pinned last — two chips reading "Unassigned" would just look broken.
  const defaultCategoryId = useMemo(
    () => categories.find((c) => c.isDefault)?.id,
    [categories]
  );
  const isUnassigned = (recipe: Recipe) => !recipe.categoryId || recipe.categoryId === defaultCategoryId;

  // Only categories actually in use by the visible recipes. Add-on scoped
  // categories are BYO choices, never dishes, so they are excluded.
  const relevantCategories = useMemo(() => {
    const usedIds = new Set(menuFilteredRecipes.map((r) => r.categoryId).filter(Boolean));
    return categories.filter(
      (c) => (c.scope ?? 'revenue') !== 'addon' && !c.isDefault && usedIds.has(c.id)
    );
  }, [menuFilteredRecipes, categories]);

  const hasUnassignedRecipes = useMemo(
    () => menuFilteredRecipes.some(isUnassigned),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuFilteredRecipes, defaultCategoryId]
  );

  // Reset to "All" when the selected category is no longer offered (menu filter
  // changed, or the last recipe in it was re-linked) — otherwise the page shows
  // an empty grid with no visibly active chip.
  useEffect(() => {
    if (selectedCategoryId === 'all') return;
    const stillAvailable = selectedCategoryId === RECIPES_UNASSIGNED_CATEGORY
      ? hasUnassignedRecipes
      : relevantCategories.some((c) => c.id === selectedCategoryId);
    if (!stillAvailable) {
      setSelectedCategoryId('all');
    }
  }, [selectedCategoryId, relevantCategories, hasUnassignedRecipes, setSelectedCategoryId]);

  // Filter recipes based on category filter and search query
  const filteredRecipes = useMemo(() => menuFilteredRecipes.filter((recipe) => {
    // Category filter (single-select)
    if (selectedCategoryId === RECIPES_UNASSIGNED_CATEGORY) {
      if (!isUnassigned(recipe)) return false;
    } else if (selectedCategoryId !== 'all' && recipe.categoryId !== selectedCategoryId) {
      return false;
    }

    // Search filter (search across all language translations)
    const searchLower = searchQuery.toLowerCase();
    const { en, es, nl } = recipe.nameTranslations || {};
    return (
      (en && en.toLowerCase().includes(searchLower)) ||
      (es && es.toLowerCase().includes(searchLower)) ||
      (nl && nl.toLowerCase().includes(searchLower))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [menuFilteredRecipes, selectedCategoryId, searchQuery, defaultCategoryId]);

  // Drag is disabled while a free-text search is active: the visible set is
  // transient and the slot re-stamping below would write an order the user
  // cannot see the whole of.
  const canReorder = !searchQuery;

  // Reordering under a filter must not disturb hidden recipes: the visible
  // recipes are re-stamped onto the position slots they collectively occupied,
  // in their new visual order (proven pattern: CostPricePageNew.tsx:609-634).
  const handleReorderRecipes = async (visibleReordered: Recipe[]) => {
    const visibleIds = new Set(visibleReordered.map((r) => r.id));
    const occupiedSlots = recipes
      .filter((r) => visibleIds.has(r.id))
      .map((r) => r.position ?? 0)
      .sort((a, b) => a - b);

    const updates = visibleReordered.map((recipe, i) => ({ id: recipe.id, position: occupiedSlots[i] }));

    // Apply locally first so the dropped card stays put (no snap-back)
    applyPositions(updates);

    try {
      await updateRecipePositions(updates);
      await reloadRecipes();
    } catch (error) {
      console.error('Failed to reorder recipes:', error);
      alert(t('recipes.failedToReorder'));
      await reloadRecipes();
    }
  };

  const handleSaveRecipe = async () => {
    await reloadRecipes();
    setIsCreateModalOpen(false);
  };

  if (!hasAccess) {
    return <Navigate to="/" />;
  }

  const handleAutomationSuccess = async () => {
    await reloadRecipes();
    setShowAutomationModal(false);
  };

  const handleRecipeClick = (recipeId: string) => {
    navigate(`/recipes/${recipeId}`);
  };

  if (isAllRestaurants) {
    return (
      <div className="p-4 max-w-7xl mx-auto">
        <MobilePageHeader title={t('recipes.title')} right={<RestaurantSelector />} />
        <div className="mb-6">
          <div className="hidden lg:flex items-center gap-3 mb-4">
            <ChefHat className="w-8 h-8 text-[#0E8965] dark:text-[#0E8965]" />
            <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText)}>{t('recipes.title')}</h1>
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
          <p className="text-yellow-800 dark:text-yellow-200">
            {t('recipes.pleaseSelectSpecificRestaurant')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <MobilePageHeader title={t('recipes.title')} right={<RestaurantSelector />} />
      {/* Header */}
      <div className="w-full flex flex-col gap-4 min-[1050px]:flex-row min-[1050px]:justify-between min-[1050px]:items-center relative pb-6">
        {/* Title */}
        <h2 className={cn(xxLargeTextSize, semiBoldText, primaryText, 'hidden lg:block')}>
          {t('recipes.title')}
        </h2>

        {/* Actions */}
        <div className="flex w-full min-[1050px]:w-fit flex-col gap-3 min-[650px]:flex-row min-[650px]:flex-nowrap min-[650px]:items-center">
          {/* Search input */}
          <div className="relative flex-1 min-[650px]:flex-none min-[650px]:w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085] dark:text-textTertiary-dark" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('recipes.searchPlaceholder') || 'Search recipes...'}
              className={cn(
                "w-full h-11 pl-10 pr-4 rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark",
                "bg-white dark:bg-background-dark",
                "text-[14px] leading-5 text-[#344054] dark:text-textSecondary-dark",
                "placeholder:text-[#667085] dark:placeholder:text-textTertiary-dark",
                "focus:outline-none focus:ring-2 focus:ring-[#0E8965]/20 dark:focus:ring-[#0E8965]/20 focus:border-[#0E8965]",
                "transition-colors"
              )}
            />
          </div>

          {/* Menu filter (always shown so users can pick "Active Dishes" even with no menus) */}
          <MenuFilterDropdown
            menus={menus}
            selected={menuFilters}
            onChange={setMenuFilters}
          />

          {/* Automation button */}
          {hasRecipeAutomationAccess && (
            <button
              onClick={() => setShowAutomationModal(true)}
              className={cn(
                "flex items-center justify-center gap-2 rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark",
                "bg-white dark:bg-background-dark px-3 py-[11px] flex-shrink-0",
                "hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors"
              )}
            >
              <Sparkles className="w-5 h-5 text-[#667085] dark:text-textTertiary-dark" />
              <span className="text-[#344054] dark:text-textSecondary-dark text-[14px] leading-5 font-semibold text-nowrap">
                {t('recipes.automation')}
              </span>
            </button>
          )}

          {/* Delete all button (super admin impersonation only) */}
          {canDeleteAllRecipes && (
            <button
              onClick={() => setShowDeleteAllModal(true)}
              className={cn(
                "flex items-center justify-center gap-2 rounded-xl border border-red-200 dark:border-red-900/50",
                "bg-white dark:bg-background-dark px-3 py-[11px] flex-shrink-0",
                "hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              )}
            >
              <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              <span className="text-red-600 dark:text-red-400 text-[14px] leading-5 font-semibold text-nowrap">
                {t('recipes.deleteAll')}
              </span>
            </button>
          )}

          <RecipeViewerMenu restaurantId={restaurantId} />

          {/* Add new button */}
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className={cn(
              "flex items-center justify-center gap-2 text-nowrap rounded-xl px-[13.5px] h-11",
              "text-sm font-semibold bg-[#0E8965] dark:bg-[#0E8965] text-white dark:text-white",
              "hover:bg-[#047857] transition-colors flex-shrink-0"
            )}
          >
            <Plus className="w-5 h-5" />
            <span className="text-white dark:text-white text-[16px] leading-6 font-semibold">{t('recipes.addNew')}</span>
          </button>
        </div>
      </div>

      {/* Category filter (single-select) — only categories actually in use.
          Its own full-width row: the header row above is already nowrap with a
          search box and up to three buttons, and squeezing a scrolling chip
          strip in there collapses them on desktop. */}
      {(relevantCategories.length > 0 || hasUnassignedRecipes) && (
        <div className="flex pb-6">
          <div className="hide-scrollbar inline-flex max-w-full overflow-auto rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark bg-white dark:bg-background-dark">
            <CategoryChip
              label={t('filter.allCategories')}
              isActive={selectedCategoryId === 'all'}
              onClick={() => setSelectedCategoryId('all')}
              first
            />
            {relevantCategories.map((category) => (
              <CategoryChip
                key={category.id}
                label={category.name}
                isActive={selectedCategoryId === category.id}
                onClick={() => setSelectedCategoryId(category.id)}
              />
            ))}
            {hasUnassignedRecipes && (
              <CategoryChip
                label={t('common.unassigned')}
                isActive={selectedCategoryId === RECIPES_UNASSIGNED_CATEGORY}
                onClick={() => setSelectedCategoryId(RECIPES_UNASSIGNED_CATEGORY)}
              />
            )}
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-[#0E8965]/20 dark:border-[#0E8965]/20 border-t-[#0E8965] dark:border-t-[#0E8965] rounded-full animate-spin" />
            <p className={cn(baseTextSize, tertiaryText)}>{t('common.loading')}</p>
          </div>
        </div>
      ) : filteredRecipes.length === 0 ? (() => {
        // Distinguish four empty-state causes so users don't think their recipes are gone:
        //  1. Genuinely no recipes exist for this restaurant.
        //  2. A search query filters them all out.
        //  3. The category filter filters them all out.
        //  4. The menu filter (specific menus or "active dishes") filters them all out.
        // The "Create first recipe" CTA only makes sense for case 1.
        const isCategoryFilterNarrowing = menuFilteredRecipes.length > 0 && selectedCategoryId !== 'all' && !searchQuery;
        const isMenuFilterNarrowing = !isCategoryFilterNarrowing && recipes.length > 0 && menuMode !== 'all' && !searchQuery;
        const showCreateCta = !searchQuery && recipes.length === 0;

        const title = searchQuery
          ? t('recipes.noRecipesFound')
          : isCategoryFilterNarrowing
          ? t('recipes.noRecipesInCategory')
          : isMenuFilterNarrowing
          ? t('recipes.noRecipesInMenu')
          : t('recipes.noRecipesYet');

        const description = searchQuery
          ? t('recipes.tryDifferentSearch')
          : isCategoryFilterNarrowing
          ? t('recipes.noRecipesInCategoryDescription')
          : isMenuFilterNarrowing
          ? t('recipes.noRecipesInMenuDescription')
          : t('recipes.createFirstRecipeDescription');

        return (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 rounded-full bg-[#F2F4F7] dark:bg-zinc-800 flex items-center justify-center mb-4">
              <ChefHat className="w-10 h-10 text-[#667085] dark:text-textTertiary-dark" />
            </div>
            <p className={cn(baseTextSize, semiBoldText, primaryText, 'mb-2')}>{title}</p>
            <p className={cn(smallTextSize, secondaryText, 'text-center max-w-md')}>{description}</p>
            {showCreateCta && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className={cn(
                  "mt-6 flex items-center gap-2 px-4 py-2.5 rounded-xl",
                  "bg-[#0E8965] dark:bg-[#0E8965] text-white dark:text-white font-semibold",
                  "hover:bg-[#047857] transition-colors"
                )}
              >
                <Plus className="w-5 h-5" />
                <span>{t('recipes.createFirstRecipe')}</span>
              </button>
            )}
          </div>
        );
      })() : (
        <SortableList
          items={filteredRecipes}
          getId={(recipe) => recipe.id}
          onReorder={handleReorderRecipes}
          strategy={rectSortingStrategy}
        >
          <div className="grid grid-cols-1 min-[650px]:grid-cols-2 min-[1050px]:grid-cols-3 gap-4">
            {filteredRecipes.map((recipe) => (
              <RecipeCard
                key={recipe.id}
                recipe={recipe}
                category={recipe.categoryId ? categoryById.get(recipe.categoryId) : undefined}
                draggable={canReorder}
                onClick={() => handleRecipeClick(recipe.id)}
              />
            ))}
          </div>
        </SortableList>
      )}

      {/* Create Recipe Modal */}
      {restaurantId && (
        <SaveRecipeModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          mode="create"
          restaurantId={restaurantId}
          onSave={handleSaveRecipe}
        />
      )}

      {/* Recipe Automation Modal */}
      {showAutomationModal && restaurantId && (
        <RecipeAutomationModal
          restaurantId={restaurantId}
          defaultMenuId={menuMode === 'menus' && menuFilters.length === 1 ? menuFilters[0] : undefined}
          onClose={() => setShowAutomationModal(false)}
          onSuccess={handleAutomationSuccess}
        />
      )}

      {/* Delete All Recipes Modal */}
      {canDeleteAllRecipes && showDeleteAllModal && restaurantId && (
        <RecipeDeleteAllModal
          restaurantId={restaurantId}
          recipeCount={recipes.length}
          onClose={() => setShowDeleteAllModal(false)}
          onSuccess={() => setShowDeleteAllModal(false)}
        />
      )}
    </div>
  );
};

// One chip of the single-select category row (segmented control, same look as
// the cost price page tabs at CostPriceTable.tsx:328-381).
const CategoryChip = ({
  label,
  isActive,
  onClick,
  first = false,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
  first?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "px-3 py-2.5 text-nowrap text-[14px] leading-5 font-semibold",
      !first && "border-l border-[#E5E7EB] dark:border-borderPrimary-dark",
      isActive
        ? "bg-[#EDFCF5] dark:bg-[#0E8965]/20 text-[#0E8965] dark:text-[#0E8965]"
        : "bg-white dark:bg-background-dark text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-neutral-800"
    )}
  >
    {label}
  </button>
);

// Updated RecipeCard component with matching design
interface RecipeCardProps {
  recipe: Recipe;
  category?: CostPriceCategory;
  draggable: boolean;
  onClick: () => void;
}

// Language availability indicator component
const LanguageIndicator = ({ nameTranslations }: { nameTranslations: Recipe['nameTranslations'] }) => {
  const languages: { code: SupportedLanguage; label: string }[] = [
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' },
    { code: 'nl', label: 'NL' },
  ];

  return (
    <div className="flex gap-1">
      {languages.map(lang => (
        <span
          key={lang.code}
          className={cn(
            "text-[10px] px-1.5 py-0.5 rounded font-medium",
            nameTranslations?.[lang.code]
              ? "bg-[#ECFDF3] text-[#027A48] dark:bg-green-900/20 dark:text-green-400"
              : "bg-[#F2F4F7] text-[#98A2B3] dark:bg-zinc-800 dark:text-zinc-500"
          )}
        >
          {lang.label}
        </span>
      ))}
    </div>
  );
};

const RecipeCard = ({ recipe, category, draggable, onClick }: RecipeCardProps) => {
  const { t, i18n } = useTranslation();
  const lang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;
  const firstImage = recipe.images && recipe.images.length > 0 ? recipe.images[0] : null;
  const recipeName = getTranslatedText(recipe.nameTranslations, lang);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: recipe.id,
    disabled: !draggable,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      className={cn(
        "bg-white dark:bg-background-dark rounded-xl border border-transparent dark:border-borderPrimary-dark",
        "cursor-pointer transition-all duration-200",
        "hover:shadow-md dark:hover:shadow-lg hover:border-[#E5E7EB]",
        "overflow-hidden group"
      )}
    >
      {/* Image */}
      <div className="relative overflow-hidden">
        {/* Drag handle — the card root is a single clickable div, so the handle
            needs touch-none (pointer sensor) and stopPropagation (no navigate). */}
        {draggable && (
          <button
            {...attributes}
            {...listeners}
            type="button"
            onClick={(e) => e.stopPropagation()}
            aria-label={t('recipes.dragToReorder')}
            className={cn(
              "absolute top-2 left-2 z-10 p-1.5 rounded-lg",
              "bg-white/90 dark:bg-black/60 backdrop-blur-sm",
              "touch-none cursor-grab active:cursor-grabbing"
            )}
          >
            <GripVertical className="w-4 h-4 text-[#667085] dark:text-textTertiary-dark" />
          </button>
        )}

        {firstImage ? (
          <img
            src={firstImage}
            alt={recipeName}
            className="w-full h-44 object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-44 bg-gradient-to-br from-[#F2F4F7] to-[#E4E7EC] dark:from-zinc-800 dark:to-zinc-900 flex items-center justify-center">
            <ChefHat className="w-12 h-12 text-[#98A2B3] dark:text-zinc-600" />
          </div>
        )}

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 dark:from-black/40 via-transparent dark:via-transparent to-transparent dark:to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Recipe Name + Language Indicator */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className={cn(baseTextSize, semiBoldText, primaryText, 'line-clamp-1 flex-1')}>
            {recipeName}
          </h3>
          <LanguageIndicator nameTranslations={recipe.nameTranslations} />
        </div>

        {/* Meta info */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-1.5">
            <Users className="w-4 h-4 text-[#667085] dark:text-textTertiary-dark" />
            <span className={cn(smallTextSize, secondaryText)}>
              {recipe.components.length} {recipe.components.length === 1 ? t('recipes.component') : t('recipes.components')}
            </span>
          </div>
          {recipe.menuItemName && (
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-[#0E8965] dark:text-[#0E8965]" />
              <span className={cn(smallTextSize, 'text-[#0E8965] dark:text-[#0E8965] font-medium')}>
                {t('recipes.linked')}
              </span>
            </div>
          )}
        </div>

        {/* Tags row */}
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Category (never blank — an unlinked recipe shows the muted "Unassigned") */}
          <CategoryBadge
            categoryName={category?.name || 'Uncategorized'}
            categoryColor={category?.color}
            isDefaultCategory={category?.isDefault}
          />

          {/* Allergies */}
          {recipe.allergies && recipe.allergies.length > 0 && (
            <>
              {recipe.allergies.slice(0, 2).map((allergy, index) => (
                <span
                  key={`allergy-${index}`}
                  className={cn(
                    "text-xs px-2 py-0.5 rounded-md font-medium",
                    "bg-[#FEF3F2] text-[#B42318] dark:bg-red-900/20 dark:text-red-400"
                  )}
                >
                  {t(`allergyKeys.${toTranslationKey(allergy)}`, allergy)}
                </span>
              ))}
              {recipe.allergies.length > 2 && (
                <span className={cn("text-xs px-2 py-0.5 rounded-md", secondaryText, "bg-[#F2F4F7] dark:bg-zinc-800")}>
                  +{recipe.allergies.length - 2}
                </span>
              )}
            </>
          )}

          {/* Diets */}
          {recipe.diets && recipe.diets.length > 0 && (
            <>
              {recipe.diets.slice(0, 2).map((diet, index) => (
                <span
                  key={`diet-${index}`}
                  className={cn(
                    "text-xs px-2 py-0.5 rounded-md font-medium",
                    "bg-[#ECFDF3] text-[#027A48] dark:bg-green-900/20 dark:text-green-400"
                  )}
                >
                  {t(`dietKeys.${toTranslationKey(diet)}`, diet)}
                </span>
              ))}
              {recipe.diets.length > 2 && (
                <span className={cn("text-xs px-2 py-0.5 rounded-md", secondaryText, "bg-[#F2F4F7] dark:bg-zinc-800")}>
                  +{recipe.diets.length - 2}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipesPage;
